import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ArrowDown, ArrowUp } from "lucide-react";
import type { AlbumBrowseItem } from "#shared/schemas/album.ts";
import { EmptyState } from "@web/components/EmptyState.tsx";
import { EntityAvatar } from "@web/components/EntityAvatar.tsx";
import { Skeleton } from "@web/components/ui/skeleton.tsx";
import { QueryErrorState } from "@web/features/diagnostics/QueryErrorState.tsx";
import { ArtistNamesCell } from "@web/features/grid/ArtistNamesCell.tsx";
import {
  useAlbumBrowse,
  useArtists,
  useGenresFlat,
} from "@web/features/library/api.ts";
import { FilterBar } from "@web/features/library/FilterBar.tsx";
import {
  expandGenreSubtree,
  formatIdList,
  parseIdList,
} from "@web/features/library/filterUrl.ts";
import { useOpenDetail } from "@web/features/detail/useOpenDetail.ts";
import { cn } from "@web/lib/utils.ts";

type SortKey = "name" | "artists" | "track_count" | "album_type" | "release_year";

function artistSortKey(row: AlbumBrowseItem): string {
  return row.artists
    .map((a) => a.name ?? "")
    .join(", ");
}

type SortState = { key: SortKey; dir: "asc" | "desc" };

const DEFAULT_SORT: SortState = { key: "track_count", dir: "desc" };

const TYPE_LABEL: Record<string, string> = {
  album: "Album",
  single: "Single",
  compilation: "Compilation",
};

function matchHint(row: AlbumBrowseItem): string | null {
  if (!row.match_via || row.match_via === "name") return null;
  const kind = row.match_via === "artist" ? "artist" : "track";
  return row.match_label ? `via ${kind} · ${row.match_label}` : `via ${kind}`;
}

function compareRows(a: AlbumBrowseItem, b: AlbumBrowseItem, sort: SortState): number {
  const dir = sort.dir === "asc" ? 1 : -1;
  switch (sort.key) {
    case "name":
      return dir * (a.name ?? "").localeCompare(b.name ?? "", undefined, { sensitivity: "base" });
    case "artists":
      return dir * artistSortKey(a).localeCompare(artistSortKey(b), undefined, {
        sensitivity: "base",
      });
    case "track_count":
      return dir * (a.track_count - b.track_count);
    case "album_type":
      return dir * (a.album_type ?? "").localeCompare(b.album_type ?? "", undefined, {
        sensitivity: "base",
      });
    case "release_year": {
      const ay = a.release_year ?? 0;
      const by = b.release_year ?? 0;
      return dir * (ay - by);
    }
  }
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  align = "left",
}: {
  label: string;
  sortKey: SortKey;
  sort: SortState;
  onSort: (key: SortKey) => void;
  align?: "left" | "right";
}) {
  const active = sort.key === sortKey;
  return (
    <button
      type="button"
      className={cn(
        "flex w-full items-center gap-1 px-3 text-xs font-semibold tracking-wider text-faint uppercase",
        align === "right" ? "justify-end" : "justify-start",
        active ? "text-muted" : "hover:text-muted",
      )}
      onClick={() => onSort(sortKey)}
    >
      {label}
      {active &&
        (sort.dir === "asc" ? (
          <ArrowUp className="size-3" aria-hidden />
        ) : (
          <ArrowDown className="size-3" aria-hidden />
        ))}
    </button>
  );
}

function patchIdParam(next: URLSearchParams, key: string, ids: readonly string[]) {
  const formatted = formatIdList(ids);
  if (formatted) next.set(key, formatted);
  else next.delete(key);
}

export function AlbumsPage() {
  const openDetail = useOpenDetail();
  const navigate = useNavigate();
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const qParam = searchParams.get("q") ?? "";
  const artistIds = useMemo(
    () => parseIdList(searchParams.get("artist")),
    [searchParams],
  );
  const genreIds = useMemo(
    () => parseIdList(searchParams.get("genre")),
    [searchParams],
  );
  const [draftQ, setDraftQ] = useState(qParam);
  const [debouncedQ, setDebouncedQ] = useState(qParam);
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const browse = useAlbumBrowse(debouncedQ);
  const artists = useArtists();
  const genres = useGenresFlat();

  useEffect(() => {
    setDraftQ(qParam);
  }, [qParam]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (draftQ.trim()) next.set("q", draftQ.trim());
      else next.delete("q");
      if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
      setDebouncedQ(draftQ.trim());
    }, 200);
    return () => window.clearTimeout(handle);
  }, [draftQ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    };
  }, []);

  const artistOptions = useMemo(
    () => (artists.data ?? []).map((a) => ({ id: a.id, label: a.name ?? a.id })),
    [artists.data],
  );
  const genreOptions = useMemo(
    () => (genres.data ?? []).map((g) => ({ id: g.id, label: g.name })),
    [genres.data],
  );
  const genreMatch = useMemo(() => {
    if (genreIds.length === 0) return null;
    return expandGenreSubtree(genreIds, genres.data ?? []);
  }, [genreIds, genres.data]);
  const artistSet = useMemo(() => new Set(artistIds), [artistIds]);

  const rows = useMemo(() => {
    let next = [...(browse.data ?? [])];
    if (artistSet.size > 0) {
      next = next.filter((row) => row.artists.some((a) => artistSet.has(a.id)));
    }
    if (genreMatch) {
      next = next.filter((row) => (row.genre_ids ?? []).some((id) => genreMatch.has(id)));
    }
    next.sort((a, b) => {
      if (debouncedQ) {
        const aName = a.match_via === "name" ? 0 : 1;
        const bName = b.match_via === "name" ? 0 : 1;
        if (aName !== bName) return aName - bName;
      }
      return compareRows(a, b, sort);
    });
    return next;
  }, [browse.data, debouncedQ, sort, artistSet, genreMatch]);

  function setFacet(key: "artist" | "genre", ids: string[]) {
    const next = new URLSearchParams(searchParams);
    patchIdParam(next, key, ids);
    setSearchParams(next, { replace: true });
  }

  function clearFilters() {
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    next.delete("artist");
    next.delete("genre");
    setDraftQ("");
    setDebouncedQ("");
    setSearchParams(next, { replace: true });
  }

  function toggleSort(key: SortKey) {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      const defaultDesc = key === "track_count" || key === "release_year";
      return { key, dir: defaultDesc ? "desc" : "asc" };
    });
  }

  function openSheet(album: AlbumBrowseItem) {
    openDetail(
      { type: "album", id: album.id, label: album.name ?? "Album" },
      { mode: "reset" },
    );
  }

  function openGrid(album: AlbumBrowseItem) {
    void navigate(`/all?album=${encodeURIComponent(album.id)}`);
  }

  function onRowClick(album: AlbumBrowseItem) {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      openSheet(album);
    }, 250);
  }

  function onRowDoubleClick(album: AlbumBrowseItem) {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    openGrid(album);
  }

  const isFiltered =
    Boolean(debouncedQ) || artistIds.length > 0 || genreIds.length > 0;
  const gridCols = "minmax(14rem, 2fr) minmax(10rem, 1.5fr) 5rem 7rem 4.5rem";

  return (
    <div className="flex h-full flex-col">
      <header className="flex shrink-0 flex-col gap-3 border-b border-line px-5 py-4">
        <div className="min-w-0">
          <p className="text-xs font-semibold tracking-wider text-faint uppercase">Browse</p>
          <h1 className="text-2xl font-semibold tracking-tight">Albums</h1>
          <p className="mt-1 text-sm text-muted">
            {browse.isLoading
              ? "Loading…"
              : isFiltered
                ? `${rows.length.toLocaleString()} matched`
                : `${rows.length.toLocaleString()} in your library`}
          </p>
        </div>

        <FilterBar
          searchValue={draftQ}
          onSearchChange={setDraftQ}
          searchPlaceholder="Search by album, artist, or track"
          artistOptions={artistOptions}
          genreOptions={genreOptions}
          artistIds={artistIds}
          genreIds={genreIds}
          onArtistIdsChange={(ids) => setFacet("artist", ids)}
          onGenreIdsChange={(ids) => setFacet("genre", ids)}
          onClearAll={isFiltered ? clearFilters : undefined}
        />
      </header>

      <div className="min-h-0 flex-1 overflow-auto p-4">
        {browse.isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-11 w-full rounded-md" />
            ))}
          </div>
        )}

        {browse.isError && (
          <QueryErrorState
            error={browse.error}
            onRetry={() => void browse.refetch()}
            actionLabel="load albums"
          />
        )}

        {browse.isSuccess && rows.length === 0 && (
          <EmptyState
            title={isFiltered ? "No matches" : "No albums yet"}
            description={
              isFiltered
                ? "Try a different search, or clear the filter."
                : "Sync from Spotify to mirror albums into CoyKoi."
            }
          />
        )}

        {browse.isSuccess && rows.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-line">
            <div
              className="grid border-b border-line bg-panel py-2"
              style={{ gridTemplateColumns: gridCols }}
            >
              <SortHeader label="Name" sortKey="name" sort={sort} onSort={toggleSort} />
              <SortHeader label="Artists" sortKey="artists" sort={sort} onSort={toggleSort} />
              <SortHeader
                label="Tracks"
                sortKey="track_count"
                sort={sort}
                onSort={toggleSort}
                align="right"
              />
              <SortHeader label="Type" sortKey="album_type" sort={sort} onSort={toggleSort} />
              <SortHeader
                label="Year"
                sortKey="release_year"
                sort={sort}
                onSort={toggleSort}
                align="right"
              />
            </div>

            <ul className="divide-y divide-line">
              {rows.map((album) => {
                const via = matchHint(album);
                return (
                  <li key={album.id}>
                    {/* div + role=button: ArtistNamesCell nests real <button>s */}
                    <div
                      role="button"
                      tabIndex={0}
                      className={cn(
                        "grid w-full cursor-pointer items-center py-2 text-left text-sm transition-colors",
                        "hover:bg-row-hover focus-visible:bg-row-hover focus-visible:outline-none",
                      )}
                      style={{ gridTemplateColumns: gridCols }}
                      onClick={() => onRowClick(album)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onRowClick(album);
                        }
                      }}
                      onDoubleClick={(e) => {
                        e.preventDefault();
                        onRowDoubleClick(album);
                      }}
                    >
                      <div className="flex min-w-0 items-center gap-3 px-3">
                        <EntityAvatar src={album.cover_url} alt="" size="sm" rounded="sm" />
                        <span className="min-w-0">
                          <span className="block truncate font-medium">
                            {album.name ?? "Unknown album"}
                          </span>
                          {via && (
                            <span className="block truncate text-xs text-faint">{via}</span>
                          )}
                        </span>
                      </div>
                      <div className="min-w-0 truncate px-3">
                        <ArtistNamesCell artists={album.artists} />
                      </div>
                      <div className="px-3 text-right font-mono tabular-nums text-muted">
                        {album.track_count.toLocaleString()}
                      </div>
                      <div className="truncate px-3 capitalize text-muted">
                        {album.album_type
                          ? (TYPE_LABEL[album.album_type] ?? album.album_type)
                          : "—"}
                      </div>
                      <div className="px-3 text-right font-mono tabular-nums text-muted">
                        {album.release_year ?? "—"}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
